import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import Loading from '../../loading/loadingPage'
import { SEVERITY, SEVERITY_ORDER } from '../makeReady/severity'
import FleetHealthDonut from './FleetHealthDonut'
import TrackedItemCard from './TrackedItemCard'
import UntrackedItemCard from './UntrackedItemCard'
import AttentionFeed from './AttentionFeed'
import useMakeReadyBoard from './useMakeReadyBoard'
import StoresPanel, { useKitStores } from './StoresPanel'
import { BOARD_GROUPS, groupMeta } from './BoardLocation'

// The Untracked chip is deliberately styled apart from the health colours -
// it's a coverage gap, not a state of the kit.
const untrackedChipSx = (active) => ({
  bgcolor: active ? '#F1F5F9' : 'transparent',
  color: active ? '#64748B' : SEVERITY.untracked.hex,
  border: `1px dashed ${SEVERITY.untracked.hex}`,
  fontWeight: 600,
})

// The board opens on what needs action now; everything else is a chip away.
const DEFAULT_LEVELS = ['expired', 'missing']

const sameLevels = (a, b) =>
  a.length === b.length && a.every((level) => b.includes(level))

const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

const MakeReadyOverview = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  // 'stores' is a board tab but not a BOARD_GROUPS member - its cards are
  // kit stores (StoresPanel), and clicking one drills into the ordinary
  // bags/assets tabs with ?store= scoping every row and count.
  const isStores = searchParams.get('group') === 'stores'
  const group = groupMeta(searchParams.get('group')).key
  const storeFilter = searchParams.get('store') || ''
  const setGroup = (key) =>
    setSearchParams(key === 'vehicles' ? {} : { group: key }, { replace: true })

  const [activeLevels, setActiveLevels] = useState(DEFAULT_LEVELS)
  const [searchInput, setSearchInput] = useState('')
  const search = useDebounced(searchInput, 300)
  const [sort, setSort] = useState('worst')

  const kitStores = useKitStores()
  const activeStore = storeFilter
    ? kitStores.stores.find((s) => s._id === storeFilter)
    : null

  const {
    items,
    total,
    counts,
    loadLists,
    loading,
    loadingMore,
    fetchError,
    loadMore,
    refresh,
  } = useMakeReadyBoard({
    group,
    levels: activeLevels,
    search,
    sort,
    store: storeFilter,
    enabled: !isStores,
  })

  const meta = groupMeta(group)
  const groupCounts = counts[group] || {}
  const fleetTotal = BOARD_GROUPS.reduce(
    (sum, g) => sum + (counts[g.key]?.total || 0),
    0,
  )
  const filtered = activeLevels.length > 0

  const toggleLevel = (level) => {
    setActiveLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level],
    )
  }

  const filterSummary = useMemo(
    () => activeLevels.map((level) => SEVERITY[level].label).join(' or '),
    [activeLevels],
  )

  const emptyMessage = () => {
    if (!groupCounts.total) {
      return (
        <Alert severity="info">
          No {meta.label.toLowerCase()} are registered yet.
        </Alert>
      )
    }
    if (search.trim()) {
      return (
        <Alert severity="info">
          No {meta.label.toLowerCase()} match &ldquo;{search.trim()}&rdquo;
          {filtered ? ` with status ${filterSummary}` : ''}.
        </Alert>
      )
    }
    return (
      <Alert
        severity={sameLevels(activeLevels, DEFAULT_LEVELS) ? 'success' : 'info'}
        action={
          <Button size="small" color="inherit" onClick={() => setActiveLevels([])}>
            Show all {groupCounts.total}
          </Button>
        }
      >
        {sameLevels(activeLevels, DEFAULT_LEVELS)
          ? `No ${meta.label.toLowerCase()} have expired or missing stock.`
          : `No ${meta.label.toLowerCase()} with status ${filterSummary}.`}
      </Alert>
    )
  }

  return (
    <Stack gap={2}>
      <Paper sx={{ px: 2, pt: 1 }}>
        <Tabs
          value={isStores ? 'stores' : group}
          onChange={(_, key) => setGroup(key)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          {BOARD_GROUPS.map((g) => {
            const c = counts[g.key] || {}
            const needsAction = (c.expired || 0) + (c.missing || 0)
            return (
              <Tab
                key={g.key}
                value={g.key}
                icon={<g.Icon fontSize="small" />}
                iconPosition="start"
                sx={{ minHeight: 56 }}
                label={
                  <Stack direction="row" gap={1} alignItems="center">
                    <span>{g.label}</span>
                    <Typography variant="caption" color="text.secondary">
                      {c.total || 0}
                    </Typography>
                    {needsAction > 0 && (
                      <Chip
                        size="small"
                        label={needsAction}
                        title={`${needsAction} with expired or missing stock`}
                        sx={{
                          height: 20,
                          bgcolor: SEVERITY.expired.hex,
                          color: '#fff',
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </Stack>
                }
              />
            )
          })}
          <Tab
            value="stores"
            icon={<WarehouseIcon fontSize="small" />}
            iconPosition="start"
            sx={{ minHeight: 56 }}
            label={
              <Stack direction="row" gap={1} alignItems="center">
                <span>Stores</span>
                <Typography variant="caption" color="text.secondary">
                  {kitStores.stores.length}
                </Typography>
                {(() => {
                  const needsAction = kitStores.stores.filter((s) =>
                    ['expired', 'missing'].includes(s.worstLevel),
                  ).length
                  return needsAction > 0 ? (
                    <Chip
                      size="small"
                      label={needsAction}
                      title={`${needsAction} store(s) holding expired or missing stock`}
                      sx={{
                        height: 20,
                        bgcolor: SEVERITY.expired.hex,
                        color: '#fff',
                        fontWeight: 700,
                      }}
                    />
                  ) : null
                })()}
              </Stack>
            }
          />
        </Tabs>
      </Paper>

      {isStores && (
        <StoresPanel
          stores={kitStores.stores}
          loading={kitStores.loading}
          fetchError={kitStores.fetchError}
          refresh={kitStores.refresh}
          onOpenStore={(store) => {
            setSearchInput('')
            setActiveLevels([])
            setSearchParams(
              {
                // Land on whichever tab holds more of the store's contents;
                // the ?store chip keeps the scope when switching tabs.
                group:
                  store.bagCount >= store.assetCount - store.bagCount
                    ? 'bags'
                    : 'assets',
                store: store._id,
              },
              { replace: true },
            )
          }}
        />
      )}

      {!isStores && (
      <Paper sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={3}
          alignItems="center"
        >
          <FleetHealthDonut
            counts={groupCounts}
            activeLevels={activeLevels}
            onToggleLevel={toggleLevel}
          />
          <Stack gap={1.5} flex={1} width="100%">
            <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
              {activeStore && (
                <Chip
                  icon={<WarehouseIcon />}
                  label={`In ${activeStore.name}`}
                  onDelete={() =>
                    setSearchParams(
                      group === 'vehicles' ? {} : { group },
                      { replace: true },
                    )
                  }
                  sx={{ fontWeight: 700 }}
                />
              )}
              {SEVERITY_ORDER.map((level) => {
                const active = !filtered || activeLevels.includes(level)
                return (
                  <Chip
                    key={level}
                    label={`${SEVERITY[level].label} (${groupCounts[level] || 0})`}
                    onClick={() => toggleLevel(level)}
                    sx={
                      level === 'untracked'
                        ? untrackedChipSx(active)
                        : {
                            bgcolor: active
                              ? SEVERITY[level].hex
                              : 'transparent',
                            color: active ? '#fff' : SEVERITY[level].hex,
                            border: `1px solid ${SEVERITY[level].hex}`,
                            fontWeight: 600,
                          }
                    }
                  />
                )
              })}
              {filtered ? (
                <Button size="small" onClick={() => setActiveLevels([])}>
                  Show All statuses
                </Button>
              ) : (
                <Button
                  size="small"
                  onClick={() => setActiveLevels(DEFAULT_LEVELS)}
                >
                  Show Expired &amp; missing only
                </Button>
              )}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              <TextField
                size="small"
                label={
                  group === 'vehicles'
                    ? 'Search call sign / type'
                    : 'Search asset ID / category'
                }
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                fullWidth
              />
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Sort</InputLabel>
                <Select
                  label="Sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                >
                  <MenuItem value="worst">Worst first</MenuItem>
                  <MenuItem value="label">
                    {group === 'vehicles' ? 'Call sign' : 'Asset ID'} (A-Z)
                  </MenuItem>
                  <MenuItem value="lastChecked">
                    Last checked (oldest first)
                  </MenuItem>
                  <MenuItem value="nextExpiry">
                    Next expiry (soonest first)
                  </MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
      )}

      {!isStores && loading && items.length === 0 && <Loading hideText />}
      {!isStores && !loading && fetchError && (
        <Alert severity="error">
          Unable to load the Make Ready board right now. Please try again
          later.
        </Alert>
      )}

      {!isStores && !fetchError && (!loading || items.length > 0) && (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            {/* Keeps the old page on screen while a new filter loads. */}
            {loading && <LinearProgress sx={{ mb: 1 }} />}
            {fleetTotal === 0 ? (
              <Alert
                severity="info"
                action={
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => navigate('/make-ready-load-lists/setup')}
                  >
                    Start Setup
                  </Button>
                }
              >
                Nothing is registered yet. Make Ready monitors vehicles and
                assets against their load lists — run the setup wizard to build
                a load list, then assign it to each vehicle or asset you want
                tracked here.
              </Alert>
            ) : items.length === 0 ? (
              emptyMessage()
            ) : (
              <Stack gap={1}>
                <Typography variant="caption" color="text.secondary">
                  Showing {items.length} of {total}{' '}
                  {meta.label.toLowerCase()}
                  {filtered ? ` · ${filterSummary}` : ''}
                </Typography>
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    xs: '1fr',
                    sm: '1fr 1fr',
                    xl: '1fr 1fr 1fr',
                  }}
                  gap={2}
                >
                  {items.map((item) =>
                    item.tracked ? (
                      <TrackedItemCard key={item.id} item={item} />
                    ) : (
                      <UntrackedItemCard
                        key={item.id}
                        item={item}
                        loadLists={loadLists}
                        onAssigned={refresh}
                      />
                    ),
                  )}
                  {items.length < total && (
                    <Box gridColumn="1 / -1" textAlign="center">
                      <Button
                        variant="outlined"
                        disabled={loadingMore}
                        onClick={loadMore}
                      >
                        {loadingMore
                          ? 'Loading…'
                          : `Show more (${total - items.length} remaining)`}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Stack>
            )}
          </Grid>
          <Grid item xs={12} lg={4}>
            <AttentionFeed
              counts={counts}
              onSelect={(key, level) => {
                setSearchInput('')
                setGroup(key)
                setActiveLevels([level])
              }}
            />
          </Grid>
        </Grid>
      )}
    </Stack>
  )
}

export default MakeReadyOverview
